
import { RecurringProductEnhanced, RecurringProductFormData, RecurringProductUpdateData, convertDBToEnhanced } from '@/types/recurring-unified';
import { recurringProductRepositoryEnhanced } from '@/repositories/recurringProduct.repository.enhanced';
import { supabase } from '@/integrations/supabase/client';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import { getValidUserId } from '@/utils/validation';
import { offlineDb } from '@/db/offlineDb';

export class RecurringProductsBusinessServiceEnhanced {
  
  async getAllRecurringProducts(): Promise<RecurringProductEnhanced[]> {
    try {
      OfflineLogger.info('RecurringProductsBusinessServiceEnhanced', 'Getting all recurring products');
      
      if (networkService.isOnline()) {
        // Online: Fetch from Supabase with product details
        const { data, error } = await supabase
          .from('recurring_products')
          .select(`
            *,
            product:product_id!inner (
              name,
              default_quantity,
              default_unit,
              category:category_id (
                name,
                icon
              )
            )
          `)
          .eq('active', true)
          .order('next_purchase_date', { ascending: true });

        if (error) {
          OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.getAllRecurringProducts.online', error);
          // Fallback to offline data
          const offlineData = await recurringProductRepositoryEnhanced.getAll();
          return offlineData.map(convertDBToEnhanced);
        }

        // Map and denormalize product data for offline use
        const enrichedData = (data || []).map(item => {
          const mappedItem = {
            id: item.id,
            user_id: item.user_id,
            product_id: item.product_id,
            frequency_days: item.frequency_days,
            recurrence_group: item.recurrence_group,
            last_purchase_date: item.last_purchase_date,
            next_purchase_date: item.next_purchase_date,
            active: item.active,
            created_at: item.created_at,
            updated_at: item.updated_at,
            attributes: item.attributes || [],
            // Denormalized fields for offline
            product_name: item.product?.name || '',
            product_default_quantity: item.product?.default_quantity || 1,
            product_default_unit: item.product?.default_unit || 'pz',
            category_name: item.product?.category?.name || '',
            category_icon: item.product?.category?.icon || ''
          };
          
          return mappedItem;
        });

        // Save enriched data to local cache
        if (enrichedData.length > 0) {
          const forRepository = enrichedData.map(item => ({
            productId: item.product_id,
            frequencyDays: item.frequency_days,
            recurrenceGroup: item.recurrence_group,
            nextPurchaseDate: item.next_purchase_date,
            attributes: item.attributes,
            user_id: item.user_id,
            id: item.id,
            updated_at: item.updated_at,
            created_at: item.created_at,
            product_name: item.product_name,
            product_default_quantity: item.product_default_quantity,
            product_default_unit: item.product_default_unit,
            category_name: item.category_name,
            category_icon: item.category_icon
          }));

          await recurringProductRepositoryEnhanced.createMany(forRepository, 'synced');
        }

        return enrichedData.map(convertDBToEnhanced);
      } else {
        // Offline: Load from local database
        const offlineData = await recurringProductRepositoryEnhanced.getAll();
        return offlineData.map(convertDBToEnhanced);
      }
    } catch (error) {
      OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.getAllRecurringProducts', error);
      // Final fallback to offline data
      try {
        const offlineData = await recurringProductRepositoryEnhanced.getAll();
        return offlineData.map(convertDBToEnhanced);
      } catch (localError) {
        OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.getAllRecurringProducts.localFallback', localError);
        return [];
      }
    }
  }

  async createRecurringProduct(data: RecurringProductFormData): Promise<RecurringProductEnhanced> {
    try {
      OfflineLogger.info('RecurringProductsBusinessServiceEnhanced', `Creating recurring product for ${data.productId}`);

      const userId = getValidUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Get product details for denormalization
      let productData = null;
      if (networkService.isOnline()) {
        const { data: productDetails } = await supabase
          .from('user_products')
          .select(`
            name,
            default_quantity,
            default_unit,
            category:category_id (
              name,
              icon
            )
          `)
          .eq('id', data.productId)
          .single();
        
        productData = productDetails;
      }

      const dbFormatData = {
        user_id: userId,
        product_id: data.productId,
        frequency_days: data.frequencyDays,
        recurrence_group: data.recurrenceGroup,
        last_purchase_date: undefined,
        next_purchase_date: data.nextPurchaseDate || new Date(Date.now() + data.frequencyDays * 24 * 60 * 60 * 1000).toISOString(),
        attributes: data.attributes || [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        active: true
      };

      if (networkService.isOnline()) {
        // Check for existing product to prevent duplicates
        const { data: existing } = await supabase
          .from('recurring_products')
          .select('id')
          .eq('user_id', userId)
          .eq('product_id', data.productId)
          .eq('active', true)
          .maybeSingle();

        if (existing) {
          OfflineLogger.info('RecurringProductsBusinessServiceEnhanced', 'Product already exists as recurring');
          throw new Error('Prodotto già configurato come ricorrente');
        }

        // Online: Insert to Supabase using DB format
        const { data: insertedData, error } = await supabase
          .from('recurring_products')
          .insert([dbFormatData])
          .select()
          .single();

        if (error) {
          throw error;
        }

        // Save to local database with product details
        const formFormatData = {
          productId: data.productId,
          frequencyDays: data.frequencyDays,
          recurrenceGroup: data.recurrenceGroup,
          nextPurchaseDate: dbFormatData.next_purchase_date,
          attributes: data.attributes || [],
          user_id: userId,
          id: insertedData.id
        };

        await recurringProductRepositoryEnhanced.create(formFormatData, 'synced');

        // Return with denormalized data
        const result = {
          ...insertedData,
          product_name: productData?.name || '',
          product_default_quantity: productData?.default_quantity || 1,
          product_default_unit: productData?.default_unit || 'pz',
          category_name: productData?.category?.name || '',
          category_icon: productData?.category?.icon || ''
        };

        return convertDBToEnhanced(result);
      } else {
        // Offline: Save to local database
        const formFormatData = {
          productId: data.productId,
          frequencyDays: data.frequencyDays,
          recurrenceGroup: data.recurrenceGroup,
          nextPurchaseDate: dbFormatData.next_purchase_date,
          attributes: data.attributes || [],
          user_id: userId
        };

        const createdProduct = await recurringProductRepositoryEnhanced.create(formFormatData);
        return convertDBToEnhanced(createdProduct);
      }
    } catch (error) {
      OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.createRecurringProduct', error);
      
      // Fallback to offline creation if online fails
      if (networkService.isOnline()) {
        try {
          const userId = getValidUserId();
          const fallbackFormData = {
            productId: data.productId,
            frequencyDays: data.frequencyDays,
            recurrenceGroup: data.recurrenceGroup,
            nextPurchaseDate: data.nextPurchaseDate || new Date(Date.now() + data.frequencyDays * 24 * 60 * 60 * 1000).toISOString(),
            attributes: data.attributes || [],
            user_id: userId
          };
          
          const createdProduct = await recurringProductRepositoryEnhanced.create(fallbackFormData);
          return convertDBToEnhanced(createdProduct);
        } catch (fallbackError) {
          OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.createRecurringProduct.fallback', fallbackError);
          throw fallbackError;
        }
      }
      throw error;
    }
  }

  async updateRecurringProduct(id: string, updates: RecurringProductUpdateData): Promise<RecurringProductEnhanced | null> {
    try {
      OfflineLogger.info('RecurringProductsBusinessServiceEnhanced', `Updating recurring product ${id}`);

      // Properly map the update data for database format
      const dbUpdateData: any = {
        updated_at: new Date().toISOString()
      };

      // Handle product change and denormalized fields
      let productDetails: any = null;
      if ((updates as any).product_id !== undefined || (updates as any).productId !== undefined) {
        const newProductId = (updates as any).product_id ?? (updates as any).productId;
        dbUpdateData.product_id = newProductId;

        try {
          if (networkService.isOnline()) {
            const { data: prod } = await supabase
              .from('user_products')
              .select(`
                name,
                default_quantity,
                default_unit,
                category:category_id (
                  name,
                  icon
                )
              `)
              .eq('id', newProductId)
              .maybeSingle();
            productDetails = prod;
          } else {
            const product = await offlineDb.products.get(newProductId);
            if (product) {
              const category = product.category_id
                ? await offlineDb.categories.get(product.category_id)
                : null;
              productDetails = {
                name: product.name,
                default_quantity: product.default_quantity,
                default_unit: product.default_unit,
                category: category
                  ? { name: category.name, icon: category.icon }
                  : null
              };
            }
          }
        } catch (err) {
          OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.updateRecurringProduct.productFetch', err);
        }
      }

      // Map frequency_days correctly
      if (updates.frequency_days !== undefined) {
        dbUpdateData.frequency_days = updates.frequency_days;
      }

      // Map other fields
      if (updates.recurrence_group !== undefined) {
        dbUpdateData.recurrence_group = updates.recurrence_group;
      }
      
      if (updates.last_purchase_date !== undefined) {
        dbUpdateData.last_purchase_date = updates.last_purchase_date;
      }
      
      if (updates.next_purchase_date !== undefined) {
        dbUpdateData.next_purchase_date = updates.next_purchase_date;
      }
      
      if (updates.attributes !== undefined) {
        dbUpdateData.attributes = updates.attributes;
      }

      // Handle status -> active mapping
      if (updates.status !== undefined) {
        dbUpdateData.active = updates.status === 'active';
      }

      if (networkService.isOnline()) {
        // Online: Update in Supabase
        const { data, error } = await supabase
          .from('recurring_products')
          .update(dbUpdateData)
          .eq('id', id)
          .select()
          .maybeSingle();

        if (error) {
          OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.updateRecurringProduct.supabase', error);
          throw error;
        }

        // Update local database immediately with form format
        const formUpdateData: any = {};
        if ((updates as any).product_id !== undefined || (updates as any).productId !== undefined) {
          const newProductId = (updates as any).product_id ?? (updates as any).productId;
          formUpdateData.productId = newProductId;
          formUpdateData.product_name = productDetails?.name || '';
          formUpdateData.product_default_quantity = productDetails?.default_quantity || 1;
          formUpdateData.product_default_unit = productDetails?.default_unit || 'pz';
          formUpdateData.category_name = productDetails?.category?.name || '';
          formUpdateData.category_icon = productDetails?.category?.icon || '';
        }
        if (updates.frequency_days !== undefined) {
          formUpdateData.frequencyDays = updates.frequency_days;
        }
        if (updates.recurrence_group !== undefined) {
          formUpdateData.recurrenceGroup = updates.recurrence_group;
        }
        if (updates.next_purchase_date !== undefined) {
          formUpdateData.nextPurchaseDate = updates.next_purchase_date;
        }
        if (updates.attributes !== undefined) {
          formUpdateData.attributes = updates.attributes;
        }

        await recurringProductRepositoryEnhanced.update(id, formUpdateData);

        const result = data
          ? {
              ...data,
              product_name: productDetails?.name || '',
              product_default_quantity: productDetails?.default_quantity || 1,
              product_default_unit: productDetails?.default_unit || 'pz',
              category_name: productDetails?.category?.name || '',
              category_icon: productDetails?.category?.icon || ''
            }
          : null;

        return result ? convertDBToEnhanced(result) : null;
      } else {
        // Offline: Update in local database with form format
        const formUpdateData: any = {};
        if ((updates as any).product_id !== undefined || (updates as any).productId !== undefined) {
          const newProductId = (updates as any).product_id ?? (updates as any).productId;
          formUpdateData.productId = newProductId;
          formUpdateData.product_name = productDetails?.name || '';
          formUpdateData.product_default_quantity = productDetails?.default_quantity || 1;
          formUpdateData.product_default_unit = productDetails?.default_unit || 'pz';
          formUpdateData.category_name = productDetails?.category?.name || '';
          formUpdateData.category_icon = productDetails?.category?.icon || '';
        }
        if (updates.frequency_days !== undefined) {
          formUpdateData.frequencyDays = updates.frequency_days;
        }
        if (updates.recurrence_group !== undefined) {
          formUpdateData.recurrenceGroup = updates.recurrence_group;
        }
        if (updates.next_purchase_date !== undefined) {
          formUpdateData.nextPurchaseDate = updates.next_purchase_date;
        }
        if (updates.attributes !== undefined) {
          formUpdateData.attributes = updates.attributes;
        }

        const updatedProduct = await recurringProductRepositoryEnhanced.update(id, formUpdateData);
        return updatedProduct ? convertDBToEnhanced(updatedProduct) : null;
      }
    } catch (error) {
      OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.updateRecurringProduct', error);

      // Fallback to offline update
      try {
        const formUpdateData: any = {};
        if ((updates as any).product_id !== undefined || (updates as any).productId !== undefined) {
          const newProductId = (updates as any).product_id ?? (updates as any).productId;
          formUpdateData.productId = newProductId;
          formUpdateData.product_name = productDetails?.name || '';
          formUpdateData.product_default_quantity = productDetails?.default_quantity || 1;
          formUpdateData.product_default_unit = productDetails?.default_unit || 'pz';
          formUpdateData.category_name = productDetails?.category?.name || '';
          formUpdateData.category_icon = productDetails?.category?.icon || '';
        }
        if (updates.frequency_days !== undefined) {
          formUpdateData.frequencyDays = updates.frequency_days;
        }
        if (updates.recurrence_group !== undefined) {
          formUpdateData.recurrenceGroup = updates.recurrence_group;
        }
        if (updates.next_purchase_date !== undefined) {
          formUpdateData.nextPurchaseDate = updates.next_purchase_date;
        }
        if (updates.attributes !== undefined) {
          formUpdateData.attributes = updates.attributes;
        }

        const updatedProduct = await recurringProductRepositoryEnhanced.update(id, formUpdateData);
        return updatedProduct ? convertDBToEnhanced(updatedProduct) : null;
      } catch (fallbackError) {
        OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.updateRecurringProduct.fallback', fallbackError);
        throw fallbackError;
      }
    }
  }

  async deleteRecurringProduct(id: string): Promise<void> {
    try {
      OfflineLogger.info('RecurringProductsBusinessServiceEnhanced', `Deleting recurring product ${id}`);

      if (networkService.isOnline()) {
        // Online: Delete from Supabase
        const { error } = await supabase
          .from('recurring_products')
          .delete()
          .eq('id', id);

        if (error) {
          throw error;
        }

        // Remove from local database immediately
        await recurringProductRepositoryEnhanced.delete(id);
      } else {
        // Offline: Mark for deletion in local database
        await recurringProductRepositoryEnhanced.delete(id);
      }
    } catch (error) {
      OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.deleteRecurringProduct', error);
      
      // Fallback to offline deletion
      try {
        await recurringProductRepositoryEnhanced.delete(id);
      } catch (fallbackError) {
        OfflineLogger.error('RecurringProductsBusinessServiceEnhanced.deleteRecurringProduct.fallback', fallbackError);
        throw fallbackError;
      }
    }
  }
}

export const recurringProductsBusinessServiceEnhanced = new RecurringProductsBusinessServiceEnhanced();
